import { Component, Input, OnInit } from '@angular/core';
import { NbDialogRef, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';
import {
  CanonicalCourse,
  CanonicalEnrollment,
  MoodleDashboardService,
} from '../../../services/moodle-dashboard.service';

interface AssignableDozent {
  id: string;
  name: string;
  email: string;
}

@Component({
  selector: 'app-moodle-dashboard-course-detail',
  templateUrl: './course-detail.component.html',
  styleUrl: './detail-dialogs.scss',
})
export class MoodleDashboardCourseDetailComponent implements OnInit {
  @Input() course!: CanonicalCourse;
  loading = true;
  enrollments: CanonicalEnrollment[] = [];
  enrollmentTotal = 0;
  enrollmentPage = 0;
  enrollmentPageSize = 50;
  pageSizeOptions = [25, 50, 100];

  // Dozent assignment state
  dozents: AssignableDozent[] = [];
  selectedDozentIds: string[] = [];
  initialDozentIds: string[] = [];
  saving = false;

  constructor(
    private moodleDashboard: MoodleDashboardService,
    public ref: NbDialogRef<MoodleDashboardCourseDetailComponent>,
    private toastr: NbToastrService,
    private t: TranslateService,
  ) {}

  ngOnInit(): void {
    this.loadEnrollments();
    forkJoin({
      dozents: this.moodleDashboard.listAssignableDozents(),
      access: this.moodleDashboard.listCourseDozentAccess(this.course.id),
    }).subscribe({
      next: ({ dozents, access }) => {
        this.dozents = dozents;
        this.initialDozentIds = (access || []).map((a) => a.user_id);
        this.selectedDozentIds = [...this.initialDozentIds];
        this.loading = false;
      },
      error: () => (this.loading = false),
    });
  }

  private loadEnrollments(): void {
    forkJoin({
      enrollments: this.moodleDashboard.listCourseEnrollments(this.course.id, {
        limit: this.enrollmentPageSize,
        offset: this.enrollmentPage * this.enrollmentPageSize,
      }),
    }).subscribe({
      next: ({ enrollments }) => {
        this.enrollments = enrollments.data ?? [];
        this.enrollmentTotal = enrollments.total ?? enrollments.pagination?.total ?? this.enrollments.length;
      },
      error: () => undefined,
    });
  }

  userLabel(en: CanonicalEnrollment): string {
    const name = `${en.first_name ?? ''} ${en.last_name ?? ''}`.trim();
    return name || en.email || `Moodle-User #${en.moodle_user_id}`;
  }

  get pagedEnrollments(): CanonicalEnrollment[] {
    return this.enrollments;
  }

  onEnrollmentPageChange(page: number): void {
    const maxPage = Math.max(
      Math.ceil(this.enrollmentTotal / this.enrollmentPageSize) - 1,
      0,
    );
    this.enrollmentPage = Math.max(0, Math.min(page, maxPage));
    this.loadEnrollments();
  }

  onEnrollmentPageSizeChange(size: number): void {
    this.enrollmentPageSize = size;
    this.enrollmentPage = 0;
    this.loadEnrollments();
  }

  get dozentDirty(): boolean {
    if (this.selectedDozentIds.length !== this.initialDozentIds.length) return true;
    const sel = new Set(this.selectedDozentIds);
    return this.initialDozentIds.some((id) => !sel.has(id));
  }

  saveDozentAccess(): void {
    if (!this.dozentDirty || this.saving) return;
    this.saving = true;
    this.moodleDashboard
      .replaceCourseDozentAccess(this.course.id, this.selectedDozentIds)
      .subscribe({
        next: () => {
          this.initialDozentIds = [...this.selectedDozentIds];
          this.saving = false;
          this.toastr.success(
            this.t.instant('pages.moodle.detail.savedToast'),
            '',
          );
        },
        error: (err) => {
          this.saving = false;
          this.toastr.danger(
            err?.error?.detail || this.t.instant('toasts.error'),
            '',
          );
        },
      });
  }

  trackEn = (_: number, en: CanonicalEnrollment) => en.id;
}
